// MF-554 — Right-hand panel of the Board Builder: the selected tile (title, chart type, visible
// series, remove) or, with nothing selected, the Board itself (name, description, who sees it and
// whose default it is, menu position).
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SUMMARY_PALETTE } from '../boardTheme'
import { DEFAULT_PALETTE, PALETTES } from '../boardPalettes'
import MenuOrderDialog from '../../../Setting/School/AnalyticsChart/MenuOrder/MenuOrderDialog'
import { getAnalyticsCharts } from '../../../../../services/tenant/analytics-chart.service'
import { getBoards, getParamValues } from '../../../../../services/boards/boards.service'

/**
 * Who opens by default for each role today, other than this Board: the Analytics buttons (what
 * login uses) and the other Boards' ★ (a draft's ★ takes over when it is published).
 * @returns {Map<string, string>} roleId → name of the button / Board
 */
const loadOtherDefaults = async (board) => {
  const [charts, boards] = await Promise.all([
    getAnalyticsCharts()
      .then((r) => r?.data || [])
      .catch(() => []),
    getBoards({ limit: 200 })
      .then((d) => d?.paginatedResults || [])
      .catch(() => [])
  ])
  const holders = new Map()
  for (const c of charts) {
    if (c.engine === 'canvas' && c.data?.boardId === String(board._id)) continue
    for (const r of c.defaultForRole || []) holders.set(String(r), c.name)
  }
  for (const b of boards) {
    if (String(b._id) === String(board._id)) continue
    for (const r of b.audience?.defaultForRole || [])
      if (!holders.has(String(r))) holders.set(String(r), b.name)
  }
  return holders
}

const TYPE_LABELS = {
  bar: 'Bars',
  hbar: 'Horizontal bars',
  line: 'Line',
  area: 'Area',
  donut: 'Donut',
  kpi: 'KPI',
  table: 'Table'
}

// MF-575 — `help` is the one-line "what it does" under the title; `info` (the ⓘ) carries the longer explanation.
const Section = ({ title, help, info, children }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: help ? 0.25 : 1 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '.06em',
          textTransform: 'uppercase',
          color: SUMMARY_PALETTE.faint
        }}
      >
        {title}
      </Typography>
      {info ? (
        <Tooltip title={info} placement='left' arrow>
          <Box
            component='span'
            tabIndex={0}
            aria-label={`About ${title}`}
            sx={{ color: SUMMARY_PALETTE.faint, fontSize: 12, cursor: 'help', lineHeight: 1 }}
          >
            <FontAwesomeIcon icon={['fal', 'info-circle']} />
          </Box>
        </Tooltip>
      ) : null}
    </Box>
    {help ? <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, mb: 1 }}>{help}</Typography> : null}
    {children}
  </Box>
)

// MF-578 — a group of sections with its own heading, collapsible; the open/closed choice is remembered.
const GROUP_KEY = (id) => `boards.builder.group.${id}`
const readOpen = (id) => {
  try {
    return window.localStorage.getItem(GROUP_KEY(id)) !== 'closed'
  } catch {
    return true
  }
}
const Group = ({ id, title, children }) => {
  const [open, setOpen] = useState(() => readOpen(id))
  const toggle = () => {
    const next = !open
    setOpen(next)
    try {
      window.localStorage.setItem(GROUP_KEY(id), next ? 'open' : 'closed')
    } catch {
      /* the choice just is not remembered */
    }
  }
  return (
    <Box
      sx={{ mb: 2, border: `1px solid ${SUMMARY_PALETTE.grid}`, borderRadius: '10px', overflow: 'hidden' }}
    >
      <Box
        component='button'
        type='button'
        onClick={toggle}
        aria-expanded={open}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1,
          border: 0,
          bgcolor: '#f3f6fa',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 700,
          color: SUMMARY_PALETTE.ink
        }}
      >
        {title}
        <FontAwesomeIcon icon={['fal', open ? 'chevron-up' : 'chevron-down']} />
      </Box>
      {open ? <Box sx={{ p: 1.5, pb: 0 }}>{children}</Box> : null}
    </Box>
  )
}

// MF-578 — one option of an item: says what it expects; a year only counts once it has four digits; a text option
// offers the usual values but accepts any. Nothing is sent to the chart while the box holds half a value.
const ParamField = ({ itemCode, param, value, onCommit }) => {
  const [text, setText] = useState(value ?? '')
  useEffect(() => setText(value ?? ''), [value])
  // MF-575 — the values that really exist in this school's data (null while loading; [] = the school has none)
  const [values, setValues] = useState(null)
  useEffect(() => {
    if (!param.hasValues || !itemCode) return undefined
    let alive = true
    getParamValues(itemCode, param.key)
      .then((list) => alive && setValues(Array.isArray(list) ? list.map(String) : []))
      .catch(() => alive && setValues([]))
    return () => {
      alive = false
    }
  }, [itemCode, param.key, param.hasValues])
  const options = values?.length ? values : param.suggestions || []
  const noData = param.hasValues && values !== null && values.length === 0
  const isYear = param.type === 'year'
  const invalid = isYear && text !== '' && !/^\d{4}$/.test(String(text))
  const commit = (v) => {
    if (isYear && v !== '' && !/^\d{4}$/.test(String(v))) return
    onCommit(v === '' ? '' : isYear ? Number(v) : v)
  }
  const hint = invalid
    ? 'Four digits, for example 2026'
    : param.hint || (param.type === 'enum' && param.default ? `Default: ${param.default}` : undefined)
  if (param.type === 'enum')
    return (
      <TextField
        select
        size='small'
        fullWidth
        sx={{ mb: 1.5 }}
        label={param.label || param.key}
        value={value ?? ''}
        onChange={(e) => onCommit(e.target.value)}
        helperText={hint}
      >
        <MenuItem value=''>Default</MenuItem>
        {param.values.map((v) => (
          <MenuItem key={v} value={v}>
            {v}
          </MenuItem>
        ))}
      </TextField>
    )
  if (options.length || param.hasValues)
    return (
      <Autocomplete
        freeSolo
        size='small'
        options={options}
        loading={param.hasValues && values === null}
        inputValue={String(text)}
        onInputChange={(_, v) => setText(v)}
        onChange={(_, v) => commit(v || '')}
        onBlur={() => commit(text)}
        sx={{ mb: 1.5 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={param.label || param.key}
            helperText={
              noData ? `${hint ? `${hint} ` : ''}This school has no data for this option yet.` : hint
            }
          />
        )}
      />
    )
  return (
    <TextField
      size='small'
      fullWidth
      sx={{ mb: 1.5 }}
      label={param.label || param.key}
      value={text}
      error={invalid}
      onChange={(e) => {
        setText(e.target.value)
        if (!isYear || e.target.value === '' || /^\d{4}$/.test(e.target.value)) commit(e.target.value)
      }}
      onBlur={() => commit(text)}
      helperText={hint}
    />
  )
}

const PaletteSelect = ({ value, onChange, allowInherit = false }) => (
  <TextField
    select
    size='small'
    fullWidth
    value={value || (allowInherit ? '' : DEFAULT_PALETTE)}
    onChange={(e) => onChange(e.target.value || null)}
    // an empty value is a real choice here ("Same as the Board"): without displayEmpty the box stays blank
    SelectProps={{
      displayEmpty: true,
      renderValue: (v) => (v ? PALETTES[v]?.label || v : 'Same as the Board')
    }}
  >
    {allowInherit ? <MenuItem value=''>Same as the Board</MenuItem> : null}
    {Object.entries(PALETTES).map(([key, p]) => (
      <MenuItem key={key} value={key}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex' }}>
            {p.colors.slice(0, 6).map((c) => (
              <Box key={c} sx={{ width: 12, height: 12, bgcolor: c }} />
            ))}
          </Box>
          {p.label}
        </Box>
      </MenuItem>
    ))}
  </TextField>
)

// A colour the school picks as its primary one. Empty = the palette's own first colour.
const PrimaryColor = ({ value, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
    <input
      type='color'
      value={value || '#2A69A7'}
      onChange={(e) => onChange(e.target.value.toUpperCase())}
      style={{ width: 36, height: 28, border: 'none', background: 'none', padding: 0 }}
    />
    <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, flex: 1 }}>
      {value ? `Primary colour ${value}` : 'Primary colour: the palette’s own'}
    </Typography>
    {value ? (
      <Button size='small' sx={{ textTransform: 'none' }} onClick={() => onChange(null)}>
        Reset
      </Button>
    ) : null}
  </Box>
)

export const TileProperties = ({ tile, item, onChange, onRemove, onClose }) => {
  const o = tile.overrides || {}
  const types = item?.presentation?.types || []
  const series = item?.presentation?.series || []
  const hidden = new Set(o.hiddenSeries || [])
  const set = (patch) => onChange({ ...tile, overrides: { ...o, ...patch } })
  return (
    <Box>
      {/* The way back to the Board's own settings (name, who sees it, menu position). */}
      <Tooltip title='Back to the Board settings · Esc' placement='left'>
        <Button
          size='small'
          variant='outlined'
          fullWidth
          onClick={onClose}
          startIcon={<FontAwesomeIcon icon={['fal', 'arrow-left']} />}
          sx={{ justifyContent: 'flex-start', mb: 1.5, textTransform: 'none', fontWeight: 600 }}
        >
          Board settings
        </Button>
      </Tooltip>
      <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>Tile</Typography>
      <Group id='content' title='Content'>
        <Section title='Library item'>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{item?.title || tile.itemCode}</Typography>
          <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted }}>
            {item?.purpose || item?.description}
          </Typography>
          <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.faint, mt: 0.5 }}>
            Size {tile.w} columns × {tile.h} rows · minimum {item?.layout?.minW || 1} ×{' '}
            {item?.layout?.minH || 1}
          </Typography>
        </Section>
        <Section title='Title'>
          <TextField
            size='small'
            fullWidth
            placeholder={item?.title || 'Title'}
            value={o.title || ''}
            inputProps={{ maxLength: 120 }}
            onChange={(e) => set({ title: e.target.value })}
            helperText='Empty = the Library title'
          />
        </Section>
        <Section
          title='Description for viewers'
          info='Shown to whoever opens the board. Write what the tile shows and how to read it, in a sentence or two. Leave it empty to use the Library description.'
          help='A sentence for whoever opens the board: what this shows and how to read it.'
        >
          <TextField
            size='small'
            fullWidth
            multiline
            minRows={2}
            placeholder={item?.description || ''}
            value={o.description || ''}
            inputProps={{ maxLength: 500 }}
            onChange={(e) => set({ description: e.target.value })}
            helperText='Shown in the tile ⓘ. Empty = the Library description'
          />
        </Section>
        <Section
          title='Description placement'
          info='On hover keeps the tile clean: the text appears when someone points at the ⓘ icon. Below the title shows it always, which suits short explanations.'
        >
          <TextField
            select
            size='small'
            fullWidth
            value={o.descriptionPlacement || 'tooltip'}
            onChange={(e) => set({ descriptionPlacement: e.target.value })}
          >
            <MenuItem value='tooltip'>In the ⓘ (on hover)</MenuItem>
            <MenuItem value='below'>Below the title</MenuItem>
          </TextField>
        </Section>
        <Section
          title='Title style'
          info='Only the title of this tile changes. Medium and bold is the default everywhere; use large for the tile that headlines the board.'
          help='Size and look of the tile title.'
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              select
              size='small'
              sx={{ flex: 1 }}
              value={o.titleStyle?.size || 'md'}
              onChange={(e) => set({ titleStyle: { bold: true, ...o.titleStyle, size: e.target.value } })}
            >
              <MenuItem value='sm'>Small</MenuItem>
              <MenuItem value='md'>Medium</MenuItem>
              <MenuItem value='lg'>Large</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  size='small'
                  checked={o.titleStyle?.bold !== false}
                  onChange={(e) =>
                    set({ titleStyle: { size: 'md', ...o.titleStyle, bold: e.target.checked } })
                  }
                />
              }
              label={<Typography sx={{ fontSize: 13 }}>Bold</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size='small'
                  checked={Boolean(o.titleStyle?.italic)}
                  onChange={(e) =>
                    set({ titleStyle: { size: 'md', bold: true, ...o.titleStyle, italic: e.target.checked } })
                  }
                />
              }
              label={<Typography sx={{ fontSize: 13 }}>Italic</Typography>}
            />
          </Box>
        </Section>
      </Group>
      <Group id='appearance' title='Appearance'>
        <Section
          title='Colours'
          info='A palette is a closed set of colours chosen by Maestro. “Same as the Board” follows the palette in Board settings. The primary colour replaces the first colour of the palette, which most charts use for their main series.'
          help='Leave on “Same as the Board” unless this tile needs its own palette.'
        >
          <PaletteSelect
            value={o.colors?.palette}
            allowInherit
            onChange={(v) => set({ colors: { ...o.colors, palette: v } })}
          />
          <PrimaryColor
            value={o.colors?.primary}
            onChange={(v) => set({ colors: { ...o.colors, primary: v } })}
          />
        </Section>
        {types.length > 1 ? (
          <Section
            title='Chart type'
            info='The same data drawn another way. Table shows the numbers instead of a chart.'
          >
            <TextField
              select
              size='small'
              fullWidth
              value={o.type || item.presentation.defaultType}
              onChange={(e) => set({ type: e.target.value })}
            >
              {types.map((t) => (
                <MenuItem key={t} value={t}>
                  {TYPE_LABELS[t] || t}
                </MenuItem>
              ))}
            </TextField>
          </Section>
        ) : null}
        {item?.kind === 'chart' && types.some((t) => t !== 'table' && t !== 'kpi') ? (
          <Section
            title='Legend'
            info='Automatic shows it only when there are two or more series. Viewers can click a legend entry to hide or show that series.'
          >
            <TextField
              select
              size='small'
              fullWidth
              value={o.legend || 'auto'}
              onChange={(e) => set({ legend: e.target.value })}
              helperText='Viewers click a legend entry to hide or show that series'
            >
              <MenuItem value='auto'>Automatic (when there are 2+ series)</MenuItem>
              <MenuItem value='show'>Always show</MenuItem>
              <MenuItem value='hide'>Hide</MenuItem>
            </TextField>
          </Section>
        ) : null}
        {item?.kind === 'chart' ? (
          <Section title='Legend position'>
            <TextField
              select
              size='small'
              fullWidth
              value={o.legendPosition || 'top'}
              onChange={(e) => set({ legendPosition: e.target.value })}
            >
              <MenuItem value='top'>Top</MenuItem>
              <MenuItem value='bottom'>Bottom</MenuItem>
            </TextField>
          </Section>
        ) : null}
      </Group>
      <Group id='data' title='Data'>
        {series.length > 1 ? (
          <Section title='Series' info='Choose which series the tile draws. At least one must stay on.'>
            {series.map((s) => (
              <FormControlLabel
                key={s.key}
                control={
                  <Checkbox
                    size='small'
                    checked={!hidden.has(s.key)}
                    disabled={!hidden.has(s.key) && series.length - hidden.size <= 1}
                    onChange={(e) =>
                      set({
                        hiddenSeries: e.target.checked
                          ? [...hidden].filter((k) => k !== s.key)
                          : [...hidden, s.key]
                      })
                    }
                  />
                }
                label={<Typography sx={{ fontSize: 13 }}>{s.label}</Typography>}
              />
            ))}
          </Section>
        ) : null}
        {item?.kind === 'chart' ? (
          <>
            <Section
              title='Order of the data'
              info='As the chart comes keeps the order of the Library. By value sorts the bars; By name sorts the labels. Lines over time should stay as they come.'
            >
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  select
                  size='small'
                  sx={{ flex: 1 }}
                  value={o.order?.by || 'source'}
                  onChange={(e) => set({ order: { dir: 'desc', ...o.order, by: e.target.value } })}
                >
                  <MenuItem value='source'>As the chart comes</MenuItem>
                  <MenuItem value='value'>By value</MenuItem>
                  <MenuItem value='label'>By name</MenuItem>
                </TextField>
                <TextField
                  select
                  size='small'
                  sx={{ width: 120 }}
                  disabled={(o.order?.by || 'source') === 'source'}
                  value={o.order?.dir || 'desc'}
                  onChange={(e) => set({ order: { by: 'value', ...o.order, dir: e.target.value } })}
                >
                  <MenuItem value='desc'>Descending</MenuItem>
                  <MenuItem value='asc'>Ascending</MenuItem>
                </TextField>
              </Box>
            </Section>
            <Section
              title='Value axis range'
              info='Fixes the lowest and highest value of the axis, for example 0 to 100 for percentages, so tiles of the same board are comparable. Empty means automatic.'
            >
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size='small'
                  type='number'
                  label='From'
                  value={o.range?.min ?? ''}
                  onChange={(e) =>
                    set({ range: { ...o.range, min: e.target.value === '' ? null : Number(e.target.value) } })
                  }
                />
                <TextField
                  size='small'
                  type='number'
                  label='To'
                  value={o.range?.max ?? ''}
                  onChange={(e) =>
                    set({ range: { ...o.range, max: e.target.value === '' ? null : Number(e.target.value) } })
                  }
                />
              </Box>
              <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, mt: 0.5 }}>
                Empty = automatic
              </Typography>
            </Section>
          </>
        ) : null}
        {(item?.params || []).length ? (
          <Section
            title='Options'
            help='Fix a filter on this tile. Leave a box empty for no filter: the tile then shows everything.'
          >
            {item.params.map((p) => (
              <ParamField
                key={p.key}
                itemCode={tile.itemCode}
                param={p}
                value={o.params?.[p.key]}
                onCommit={(v) => {
                  const next = { ...(o.params || {}) }
                  if (v === '' || v === null || v === undefined) delete next[p.key]
                  else next[p.key] = v
                  set({ params: next })
                }}
              />
            ))}
          </Section>
        ) : null}
        <Section
          title='When there is no data'
          info='Hide removes the tile when it has nothing to show, and the tiles below move up. Show a message keeps the tile with an explanation, useful when its absence would confuse.'
          help='Hide the tile, or keep it and show a message.'
        >
          <TextField
            select
            size='small'
            fullWidth
            value={o.empty || 'hide'}
            onChange={(e) => set({ empty: e.target.value })}
          >
            <MenuItem value='hide'>Hide the tile</MenuItem>
            <MenuItem value='message'>Show a message</MenuItem>
          </TextField>
        </Section>
      </Group>
      <Divider sx={{ my: 2 }} />
      <Button color='error' variant='outlined' fullWidth onClick={onRemove}>
        Remove from board
      </Button>
    </Box>
  )
}

export const BoardProperties = ({ board, roles, onChange }) => {
  const [ordering, setOrdering] = useState(false)
  const [otherDefaults, setOtherDefaults] = useState(new Map())
  const [takeOver, setTakeOver] = useState(null) // { roleId, roleName, holder }
  useEffect(() => {
    loadOtherDefaults(board).then(setOtherDefaults)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board._id])
  const canOrderMenu = useSelector((state) =>
    Boolean(state?.session?.permissions?.['analytics-chart']?.updated)
  )
  const access = new Set((board.audience?.accessRole || []).map(String))
  const defaults = new Set((board.audience?.defaultForRole || []).map(String))
  const setAudience = (accessRole, defaultForRole) =>
    onChange({
      audience: {
        accessRole: [...accessRole],
        defaultForRole: [...defaultForRole].filter((r) => accessRole.has(r))
      }
    })
  return (
    <Box>
      <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>Board settings</Typography>
      <Section title='Name'>
        <TextField
          size='small'
          fullWidth
          value={board.name || ''}
          inputProps={{ maxLength: 80 }}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </Section>
      <Section title='Description'>
        <TextField
          size='small'
          fullWidth
          multiline
          minRows={2}
          value={board.description || ''}
          inputProps={{ maxLength: 500 }}
          onChange={(e) => onChange({ description: e.target.value })}
        />
      </Section>
      <Section title='Colours'>
        <PaletteSelect
          value={board.settings?.palette}
          onChange={(v) => onChange({ settings: { ...board.settings, palette: v || DEFAULT_PALETTE } })}
        />
        <PrimaryColor
          value={board.settings?.primary}
          onChange={(v) => onChange({ settings: { ...board.settings, primary: v } })}
        />
        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, mt: 0.5 }}>
          Every tile uses these unless it picks its own.
        </Typography>
      </Section>
      <Section title='Student dashboard'>
        <FormControlLabel
          control={
            <Switch
              size='small'
              checked={Boolean(board.settings?.studentFirst)}
              onChange={(e) => onChange({ settings: { ...board.settings, studentFirst: e.target.checked } })}
            />
          }
          label={<Typography sx={{ fontSize: 13 }}>Staff choose a student first</Typography>}
        />
        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted }}>
          On: staff see nothing until they pick a student (never the school’s numbers). Off: they see the
          school.
        </Typography>
      </Section>
      <Section title='Visible to (★ = opens by default)'>
        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, mb: 1 }}>
          Maestro always sees it. Students and parents only get tiles that can show their own data.
        </Typography>
        {(roles || []).map((r) => {
          const id = String(r._id)
          return (
            <Box key={id} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                sx={{ flex: 1 }}
                control={
                  <Checkbox
                    size='small'
                    checked={access.has(id)}
                    onChange={(e) => {
                      const a = new Set(access)
                      if (e.target.checked) a.add(id)
                      else a.delete(id)
                      setAudience(a, defaults)
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: 13 }}>{r.name}</Typography>}
              />
              <Tooltip
                title={
                  access.has(id)
                    ? 'Open this board by default when this role logs in'
                    : 'Make it visible first'
                }
              >
                <span>
                  <Checkbox
                    size='small'
                    icon={<span style={{ fontSize: 16, color: SUMMARY_PALETTE.faint }}>☆</span>}
                    checkedIcon={<span style={{ fontSize: 16, color: '#e0a800' }}>★</span>}
                    disabled={!access.has(id)}
                    checked={defaults.has(id)}
                    onChange={(e) => {
                      const d = new Set(defaults)
                      // one default per role: confirm before taking it from another button / Board
                      if (e.target.checked && otherDefaults.has(id)) {
                        setTakeOver({ roleId: id, roleName: r.name, holder: otherDefaults.get(id) })
                        return
                      }
                      if (e.target.checked) d.add(id)
                      else d.delete(id)
                      setAudience(access, d)
                    }}
                  />
                </span>
              </Tooltip>
            </Box>
          )
        })}
      </Section>
      <Section title='Place in the Analytics menu'>
        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, mb: 1 }}>
          {board.status === 'published'
            ? 'Drag the Analytics buttons into order.'
            : 'When published, the board goes to the end of the Analytics menu; then drag it into place.'}
        </Typography>
        {canOrderMenu ? (
          <Button
            size='small'
            variant='outlined'
            startIcon={<FontAwesomeIcon icon={['fas', 'grip-dots-vertical']} />}
            onClick={() => setOrdering(true)}
          >
            Reorder the menu
          </Button>
        ) : null}
        <MenuOrderDialog
          open={ordering}
          highlightId={board.analyticsChartId ? String(board.analyticsChartId) : undefined}
          onClose={() => setOrdering(false)}
        />
      </Section>
      <Dialog open={Boolean(takeOver)} onClose={() => setTakeOver(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Only one default per role</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            <b>{takeOver?.roleName}</b> already opens <b>{takeOver?.holder}</b> by default after logging in.
          </Typography>
          <Typography sx={{ fontSize: 14, mt: 1.5 }}>
            Make <b>{board.name}</b> the default instead? {takeOver?.holder} stops being the default
            {board.status === 'published' ? ' when you save.' : ' when this board is published.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTakeOver(null)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={() => {
              const d = new Set(defaults)
              d.add(takeOver.roleId)
              setAudience(access, d)
              setOtherDefaults((m) => {
                const next = new Map(m)
                next.delete(takeOver.roleId)
                return next
              })
              setTakeOver(null)
            }}
          >
            Make this the default
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
